import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  variant?: 'success' | 'neutral' | 'error';
  icon?: string;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

const VARIANT_STYLES = {
  success: {
    icon: 'check_circle',
    iconColor: 'var(--success-dark)',
    bgColor: 'var(--success-light)',
  },
  neutral: {
    icon: 'inbox',
    iconColor: 'var(--foreground-muted)',
    bgColor: 'var(--background-muted)',
  },
  error: {
    icon: 'error',
    iconColor: 'var(--destructive)',
    bgColor: 'var(--destructive-light)',
  },
} as const;

export function EmptyState({
  variant = 'neutral',
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  const styles = VARIANT_STYLES[variant];
  const displayIcon = icon ?? styles.icon;

  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-xl px-6 py-10 text-center',
        className,
      )}
      style={{ backgroundColor: styles.bgColor }}
    >
      <span
        className="material-symbols-outlined mb-3"
        style={{ fontSize: '40px', color: styles.iconColor, opacity: 0.8 }}
        aria-hidden="true"
      >
        {displayIcon}
      </span>
      <p
        className="text-sm font-medium"
        style={{ color: 'var(--foreground)' }}
      >
        {title}
      </p>
      {description && (
        <p
          className="mt-1 max-w-xs text-[0.8125rem]"
          style={{ color: 'var(--foreground-muted)' }}
        >
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
